import { Check } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { INTELLIGENCE_PLANS, type IntelligencePlanId } from "@/lib/intelligencePlans";
import type { TrialAccessRole } from "@/lib/trialAccess";

const CONTEXTUAL_PLAN_IDS = ["leadership_record_solo", "intelligence_solo"] as const;

export function TrialPricingDialog({
  role,
  open,
  onOpenChange,
  primaryLeaderId,
  onSelectPlan,
}: {
  role: TrialAccessRole;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  primaryLeaderId: string | null;
  onSelectPlan?: (planId: IntelligencePlanId) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Keep the experience that fits you</DialogTitle>
          <DialogDescription>
            {role === "coach"
              ? "These are the two paths for your one-leader experience. Engagement terms and billing details appear before checkout."
              : "Choose the level you want after the trial. Your existing history stays available either way."}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3 sm:grid-cols-2">
          {CONTEXTUAL_PLAN_IDS.map((planId) => {
            const plan = INTELLIGENCE_PLANS[planId];
            const continuesFullTrial = planId === "intelligence_solo";
            return (
              <div
                key={planId}
                className={`flex flex-col rounded-xl border p-4 ${
                  continuesFullTrial ? "border-primary/40 bg-primary/[0.04]" : "border-border"
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <h3 className="text-sm font-semibold text-foreground">{plan.label}</h3>
                  <Badge variant="secondary">${plan.priceUsdMonthly}/month</Badge>
                </div>
                {continuesFullTrial ? (
                  <p className="mt-2 text-xs font-medium text-primary">
                    Continue the full trial experience
                  </p>
                ) : (
                  <p className="mt-2 text-xs text-muted-foreground">
                    Keep your Leadership Record and existing history.
                  </p>
                )}
                <ul className="mt-4 flex-1 space-y-2 text-xs text-muted-foreground">
                  <li className="flex gap-2">
                    <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
                    Leadership Record remains available
                  </li>
                  {continuesFullTrial ? (
                    <li className="flex gap-2">
                      <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
                      New Intelligence, meeting capture, and Coach Veera
                    </li>
                  ) : null}
                </ul>
                <Button
                  type="button"
                  variant={continuesFullTrial ? "default" : "outline"}
                  className="mt-4 w-full"
                  disabled={role === "coach" && !primaryLeaderId}
                  onClick={() => onSelectPlan?.(planId)}
                >
                  {role === "coach" && !primaryLeaderId ? "Add a leader first" : "View this plan"}
                </Button>
              </div>
            );
          })}
        </div>

        {role === "leader" ? (
          <p className="text-xs text-muted-foreground">
            Intelligence Solo follows the current solo billing path and may require Leadership Record first.
          </p>
        ) : null}

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Keep exploring
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
